/**
 * The path with colon-prefixed parameters
 * e.g. "/posts/:id".
 */
export type Path = string;

/**
 * A query endpoint. In REST terms, one using GET.
 */
export type AppRouteQuery = {
  method: 'GET';
  path: Path;
  pathParams?: unknown;
  query?: unknown;
  response: unknown;
  permissions?: string[];
  auth?: boolean;
  /**
   * Requires the caller to hold the global super-admin role, on top of any
   * `permissions` listed above. `permissions` are company-scoped role
   * permissions and cannot express a global role, so the two are checked
   * independently - an endpoint may declare either or both.
   */
  superAdminOnly?: boolean;
  doc?: {
    deprecated?: boolean;
    description?: string;
    summary?: string;
  };
};

/**
 * A mutation endpoint. In REST terms, one using POST, PUT,
 * PATCH, or DELETE.
 */
export type AppRouteMutation = {
  method: 'POST' | 'DELETE' | 'PUT' | 'PATCH';
  path: Path;
  pathParams?: unknown;
  contentType?: 'application/json';
  body: unknown;
  query?: unknown;
  response: unknown;
  permissions?: string[];
  auth?: boolean;
  /**
   * Requires the caller to hold the global super-admin role, on top of any
   * `permissions` listed above. `permissions` are company-scoped role
   * permissions and cannot express a global role, so the two are checked
   * independently - an endpoint may declare either or both.
   */
  superAdminOnly?: boolean;
  doc?: {
    deprecated?: boolean;
    description?: string;
    summary?: string;
  };
};

/**
 * A union of all possible endpoint types.
 */
export type AppRoute = AppRouteQuery | AppRouteMutation;

/**
 * A router (or contract) in @ts-rest is a collection of more routers or
 * individual routes
 */
export type AppRouter = {
  [key: string]: AppRouter | AppRoute;
};

/**
 * Differentiate between a route and a router
 *
 * @param obj
 * @returns
 */
export const isAppRoute = (obj: AppRoute | AppRouter): obj is AppRoute => {
  return obj?.method !== undefined;
};
